import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'sb-hooks'
import { useConnect } from 'sb-redux-hook'
import { getUserFullName, links, snowplow } from 'common/helpers'
import { useIntl } from 'sb-react-intl'
import actions from 'redux/actions'

import ModalContainer from 'components/_new/modal/ModalContainer/ModalContainer'
import DialogModal from 'components/_new/modal/DialogModal/DialogModal'

import { Box, Overlay } from 'components/_new/layout'
import Text from 'components/_new/typography/Text/Text'

import messages from './messages'


type VerifyShippingAddressModalProps = {
  name: string
  title?: string | ReactIntlMessage
  text: string | ReactIntlMessage
  openEventName: string
  confirmEventName: string
}

const VerifyShippingAddressBaseModal: React.FunctionComponent<VerifyShippingAddressModalProps> = (props) => {
  const { name, title = messages.title, text, openEventName, confirmEventName } = props

  const intl = useIntl()
  const [ isProcessing, setIsProcessing ] = useState(false)
  const { shipping } = useConnect({
    shipping: 'models.me.shipping',
  })

  const handleClose = useCallback(() => {
    actions.models.modals.close(name)
  }, [ name ])

  const handleClickCorrect = useCallback(() => {
    if (confirmEventName) {
      snowplow.track(confirmEventName)
    }

    setIsProcessing(true)
    actions.models.me.confirmShippingInfo()
      .then(() => {
        actions.models.modals.close(name)
      }, () => {
        setIsProcessing(false)
      })
  }, [ name, confirmEventName ])

  useEffect(() => {
    if (openEventName) {
      snowplow.track(openEventName)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { firstName, lastName, street, apt, zipCode, state, city } = shipping

  const formattedShippingAddress = useMemo(() => (
    [
      getUserFullName({ firstName, lastName }),
      street,
      apt,
      ([ city, state, zipCode ]).filter((item) => item).join(', '),
    ]
      .filter((item) => item)
  ), [ firstName, lastName, street, apt, zipCode, state, city ])

  const leftButton = useMemo(() => ({
    title: messages.updateAddress,
    to: `${links.profile.billingAndShipping}?tab=shipping`,
    onClick: handleClose,
  }), [ handleClose ])

  const rightButton = useMemo(() => ({
    title: messages.addressIsCorrect,
    color: 'brandBorder',
    loading: isProcessing,
    onClick: handleClickCorrect,
  }), [ isProcessing, handleClickCorrect ])

  const message = useMemo(() => `${intl.message(text)} ${intl.message(messages.textNote)}`, [ text ])

  return (
    <ModalContainer>
      <Overlay />
      <DialogModal
        title={title}
        onClose={handleClose}
        leftButton={leftButton}
        rightButton={rightButton}
      >
        <Text message={message} html />
        <Box mt={24}>
          <Text message={messages.addressNote} />
          {
            formattedShippingAddress.map((line) => (
              <Text key={line} message={line} semibold />
            ))
          }
        </Box>
      </DialogModal>
    </ModalContainer>
  )
}


export default VerifyShippingAddressBaseModal
